import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Product } from "@/mocks/products";
import { getFlipbookUrl } from "@/lib/slug";
import DOMPurify from "dompurify";
import BuilderRenderer from "./BuilderRenderer";
import { BuilderPage } from "@/pages/admin/components/builder/types";

interface Props {
  product: Product;
}

const COMPANY_LOGO =
  "https://storage.readdy-site.link/project_files/5f2724b2-0dd8-4526-ab2c-c1f8f3f4c6d1/ff2930e2-71a8-4589-bb2f-65a564d9509d_---.png?v=e95b8b61ae78d1ec4a164b9ad07adef2";

const isVideoUrl = (url: string) => {
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  return ["mp4", "webm", "mov", "avi", "mkv"].includes(ext);
};

/* ── Print / Share / Flipbook Floater ─────────────────────────────────────── */
const PrintFloater = ({ product }: { product: Product }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(() => {
    const origin = window.location.origin;
    let publicOrigin = origin;
    if (origin.includes("readdy.cc")) {
      const canonical = document.querySelector<HTMLLinkElement>("link[rel='canonical']")?.href;
      if (canonical) { try { publicOrigin = new URL(canonical).origin; } catch { /* ignore */ } }
      else { publicOrigin = import.meta.env.VITE_PUBLIC_SITE_URL || origin; }
    }
    const realUrl = window.location.href.replace(origin, publicOrigin);
    navigator.clipboard.writeText(realUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 no-print">
      <Link
        to={getFlipbookUrl({ companyName: product.companyName, name: product.name })}
        target="_blank"
        className="flex items-center gap-2 bg-white/95 backdrop-blur-sm border border-gray-200/80 text-gray-600 text-xs font-medium px-3.5 py-2 rounded-full hover:bg-white hover:text-[#004A99] hover:border-[#004A99]/30 transition-all cursor-pointer whitespace-nowrap"
        style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
      >
        <div className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-book-open-line text-xs" />
        </div>
        Xem Flipbook
      </Link>
      <button
        onClick={handleCopyLink}
        className="flex items-center gap-2 bg-white/95 backdrop-blur-sm border border-gray-200/80 text-gray-600 text-xs font-medium px-3.5 py-2 rounded-full hover:bg-white hover:text-[#004A99] hover:border-[#004A99]/30 transition-all cursor-pointer whitespace-nowrap"
        style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
      >
        <div className="w-3.5 h-3.5 flex items-center justify-center">
          <i className={copied ? "ri-check-line text-green-600" : "ri-links-line text-xs"} />
        </div>
        {copied ? "Đã sao chép!" : "Sao chép link"}
      </button>
    </div>
  );
};

/* ── Catalog Header ─────────────────────────────────────────────────────────── */
const CatalogHeader = () => (
  <>
    <header className="border-b border-gray-100 bg-white px-8 py-4 flex items-center justify-between no-print sticky top-0 z-30">
      <img
        src={COMPANY_LOGO}
        alt="Korea Carbon Nano"
        style={{ height: 48, width: "auto", maxWidth: 260, objectFit: "contain", mixBlendMode: "multiply" }}
      />
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400 border border-gray-200 px-3 py-1.5 rounded-full leading-tight">
          Trang chi tiết<br />sản phẩm
        </span>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {(() => {
            const d = new Date();
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
          })()}
        </span>
      </div>
    </header>
    {/* Print-only header */}
    <div className="hidden print:flex items-center justify-between px-0 py-3 mb-4 border-b border-gray-200">
      <img
        src={COMPANY_LOGO}
        alt="Korea Carbon Nano"
        style={{ height: 40, width: "auto", objectFit: "contain", mixBlendMode: "multiply" }}
      />
      <div className="text-xs text-gray-500">
        Trang chi tiết sản phẩm · {(() => { const d = new Date(); const dd = String(d.getDate()).padStart(2, "0"); const mm = String(d.getMonth() + 1).padStart(2, "0"); const yyyy = d.getFullYear(); return `${dd}/${mm}/${yyyy}`; })()}
      </div>
    </div>
  </>
);

/* ── Catalog Footer ─────────────────────────────────────────────────────────── */
const CatalogFooter = () => (
  <div className="border-t border-gray-100 px-10 py-8 flex items-center justify-between flex-wrap gap-4">
    <div className="flex items-center gap-3">
      <img
        src={COMPANY_LOGO}
        alt="Korea Carbon Nano"
        style={{ height: 36, width: "auto", objectFit: "contain", mixBlendMode: "multiply" }}
      />
      <div>
        <p className="text-xs font-bold text-gray-700">Hiệp Hội Công Nghiệp Carbon Nano Hàn Quốc</p>
        <p className="text-[10px] text-gray-400">Tài liệu B2B · Chỉ dùng cho mục đích tư vấn kinh doanh</p>
      </div>
    </div>
    <div className="text-right text-[10px] text-gray-400 leading-relaxed">
      <p>bizwell@bizwell.com.vn · bizwell.com.vn</p>
      <p>+84 965 180 170</p>
      <p>+84 28 6288 4688</p>
    </div>
  </div>
);

/* ── Hero Section (full-width media banner) ────────────────────────────────── */
const CatalogHero = ({ product }: { product: Product }) => {
  const rawMediaItems = (product as unknown as Record<string, unknown>).media_items;
  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMediaItems)
    ? rawMediaItems as Array<{ url: string; type: string; is_cover?: boolean }>
    : [];

  const videoItem = mediaItems.find((m) => m.type === "video" || isVideoUrl(m.url));
  const coverItem = mediaItems.find((m) => m.is_cover && m.type === "image");
  const firstImg = mediaItems.find((m) => m.type === "image");
  const heroMedia = videoItem ?? coverItem ?? firstImg ?? (product.images[0] ? { url: product.images[0], type: "image" } : null);
  const heroUrl = heroMedia?.url ?? "";
  const heroIsVideo = heroMedia ? (heroMedia.type === "video" || isVideoUrl(heroUrl)) : false;

  if (!heroUrl) return null;

  return (
    <div className="relative w-full overflow-hidden" style={{ height: 480, backgroundColor: "#0f172a" }}>
      {heroIsVideo ? (
        <video
          src={heroUrl}
          className="w-full h-full object-cover block"
          autoPlay loop muted playsInline
          poster={product.images[0] ?? ""}
          style={{ display: "block" }}
        />
      ) : (
        <img
          src={heroUrl}
          alt={product.name}
          className="w-full h-full object-cover block"
          loading="eager"
          decoding="sync"
          style={{
            display: "block",
            imageRendering: "-webkit-optimize-contrast",
          }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs font-bold tracking-widest uppercase bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full border border-white/30">
            {product.categoryName}
          </span>
          {product.subCategoryName && (
            <span className="text-xs text-white/70">/</span>
          )}
          <span className="text-xs text-white/80 font-medium">{product.subCategoryName}</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {product.name}
        </h1>
        {product.nameKr && (
          <p className="text-white/60 text-lg">{product.nameKr}</p>
        )}
        {product.heroTagline && (
          <p className="text-white/80 text-base mt-2 max-w-2xl leading-relaxed">{product.heroTagline}</p>
        )}
      </div>
    </div>
  );
};

/* ── Media Gallery + Info Panel ────────────────────────────────────────────── */
const CatalogInfoPanel = ({ product }: { product: Product }) => {
  const [activeImg, setActiveImg] = useState(0);
  const [companyLogo, setCompanyLogo] = useState<string>(product.companyLogo ?? "");

  useEffect(() => {
    if (companyLogo || !product.companyName) return;
    supabase
      .from("companies")
      .select("logo_url")
      .ilike("name", product.companyName.trim())
      .maybeSingle()
      .then(({ data }) => { if (data?.logo_url) setCompanyLogo(data.logo_url); });
  }, [product.companyName, companyLogo]);

  const rawMediaItems = (product as unknown as Record<string, unknown>).media_items;
  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMediaItems)
    ? rawMediaItems as Array<{ url: string; type: string; is_cover?: boolean }>
    : [];

  const thumbList = mediaItems.length > 0
    ? mediaItems.map((m) => ({ url: m.url, isVideo: m.type === "video" || isVideoUrl(m.url), is_cover: m.is_cover }))
    : product.images.map((img) => ({ url: img, isVideo: false, is_cover: false }));

  const activeThumb = thumbList[activeImg] ?? thumbList[0];

  const shortDesc = product.description ?? "";
  const detailDesc = product.descriptionDetail ?? "";
  const isHtml = detailDesc.includes("<");
  const cleanHtml = isHtml
    ? DOMPurify.sanitize(detailDesc, {
        ADD_ATTR: ["target", "rel", "style", "width", "height"],
        ALLOWED_ATTR: [
          "href", "src", "alt", "title", "target", "rel",
          "style", "class", "width", "height",
          "colspan", "rowspan",
        ],
      })
    : "";
  const tags: string[] = product.tags ?? [];

  return (
    <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-10">
      <div className="flex flex-col lg:flex-row gap-10 items-start">
        {/* Left: Media gallery */}
        <div className="w-full lg:w-[55%]">
          <div
            className="rounded-2xl overflow-hidden border-2 border-gray-300 shadow-md"
            style={{
              backgroundColor: "#E8ECF3",
              backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          >
            {activeThumb?.isVideo ? (
              <video
                src={activeThumb.url}
                className="w-full block"
                controls
                playsInline
                preload="metadata"
                poster={product.images[0] ?? ""}
                style={{ display: "block", minHeight: 200 }}
              />
            ) : (
              <img
                src={activeThumb?.url ?? ""}
                alt={product.name}
                className="w-full h-auto block"
                loading="eager"
                decoding="sync"
                style={{
                  display: "block",
                  imageRendering: "-webkit-optimize-contrast",
                  minHeight: 200,
                }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            )}
          </div>
          {thumbList.length > 1 && (
            <div className="flex gap-2 flex-wrap mt-3">
              {thumbList.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer relative ${
                    activeImg === i ? "border-[#004A99] ring-2 ring-[#004A99]/20" : "border-gray-300 opacity-70 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: "#E8ECF3" }}
                >
                  {t.isVideo ? (
                    <>
                      <video src={t.url} className="w-full h-full object-contain block" muted playsInline preload="metadata" style={{ display: "block" }} />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <i className="ri-play-circle-fill text-white text-lg" />
                      </div>
                    </>
                  ) : (
                    <img
                      src={t.url}
                      alt=""
                      className="w-full h-full object-contain block p-0.5"
                      loading="eager"
                      style={{ display: "block", imageRendering: "-webkit-optimize-contrast" }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  {t.is_cover && !t.isVideo && (
                    <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-[8px] font-bold text-center py-0.5">Đại diện</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Info */}
        <div className="w-full lg:w-[45%] space-y-5">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold bg-[#EEF4FF] text-[#004A99] px-3 py-1.5 rounded-full border border-[#004A99]/20">
              {product.categoryName}
            </span>
            <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
            <span className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-full">
              {product.subCategoryName}
            </span>
          </div>

          {/* Title */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 leading-tight">{product.name}</h2>
            {product.nameKr && <p className="text-gray-400 text-sm mt-1">{product.nameKr}</p>}
          </div>

          {/* Company */}
          <div className="flex items-center gap-3 py-3 border-y border-gray-100">
            <div className="w-11 h-11 flex items-center justify-center bg-[#EEF4FF] rounded-xl flex-shrink-0 overflow-hidden">
              {companyLogo ? (
                <img src={companyLogo} alt={product.companyName} className="w-full h-full object-contain p-1" />
              ) : (
                <i className="ri-building-4-line text-[#004A99] text-xl" />
              )}
            </div>
            <div>
              <p className="text-xs text-gray-400">Doanh nghiệp cung cấp</p>
              <p className="font-semibold text-gray-900 text-sm">{product.companyName}</p>
            </div>
            <span className="ml-auto text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
              Đã xác thực
            </span>
          </div>

          {/* Descriptions */}
          {shortDesc && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Mô tả</p>
              <p className="text-gray-700 text-sm leading-relaxed font-medium">{shortDesc}</p>
            </div>
          )}
          {detailDesc && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Chi tiết</p>
              {isHtml ? (
                <div
                  className="text-gray-600 text-sm leading-relaxed w-full overflow-hidden
                    [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ul>li]:mb-1
                    [&_ol]:list-decimal [&_ol]:pl-4 [&_ol>li]:mb-1
                    [&_strong]:font-bold [&_strong]:text-gray-800
                    [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2
                    [&_img]:rounded-xl [&_img]:my-2 [&_img]:block"
                  style={{ maxWidth: '100%' }}
                  dangerouslySetInnerHTML={{ __html: cleanHtml }}
                />
              ) : (
                <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{detailDesc}</p>
              )}
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Từ khóa</p>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <span key={tag} className="text-[10px] bg-[#EEF4FF] text-[#004A99] px-2.5 py-1 rounded-full font-medium border border-[#004A99]/15">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          {(product.managerPhone || product.managerEmail) && (
            <div className="p-4 rounded-xl bg-[#F8FAFF] border border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Liên hệ</p>
              <div className="space-y-1.5">
                {product.managerPhone && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-[#004A99] text-xs" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{product.managerPhone}</span>
                  </div>
                )}
                {product.managerEmail && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-[#004A99] text-xs" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{product.managerEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ── Specs Table ────────────────────────────────────────────────────────────── */
const CatalogSpecsTable = ({ product }: { product: Product }) => {
  const raw = product as unknown as Record<string, unknown>;
  const supabaseSpecs = raw.specs as Array<{ label: string; value: string }> | undefined;

  const rows: Array<{ label: string; value: string }> = [
    { label: "Danh mục", value: product.categoryName },
    { label: "Phân loại", value: product.subCategoryName },
    { label: "Thương hiệu / Nhà sản xuất", value: product.companyName },
    ...(product.registeredDate ? [{ label: "Ngày đăng ký", value: product.registeredDate }] : []),
    ...(Array.isArray(supabaseSpecs) ? supabaseSpecs : []),
  ].filter((r) => r.value && r.value.trim() !== "");

  if (rows.length === 0) return null;

  return (
    <section className="bg-[#F8FAFE] py-14">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="mb-8">
          <p className="text-xs font-bold tracking-[0.3em] text-[#004A99] uppercase mb-1">THÔNG SỐ KỸ THUẬT</p>
          <h2 className="text-2xl font-bold text-gray-900">Chi tiết đầy đủ từ nhà sản xuất</h2>
        </div>
        <div className="rounded-2xl overflow-hidden border border-gray-100 bg-white">
          <table className="w-full">
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={`border-b border-gray-50 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                  <td className="py-3.5 px-6 w-1/3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{row.label}</span>
                  </td>
                  <td className="py-3.5 px-6">
                    <span className="text-sm font-medium text-gray-900">{row.value}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

/* ── Story Sections (legacy) ────────────────────────────────────────────────── */
const CatalogStory = ({ product }: { product: Product }) => {
  if (!product.sections || product.sections.length === 0) return null;

  return (
    <div>
      <div className="bg-gray-900 py-12 text-center">
        <p className="text-xs font-bold tracking-[0.3em] text-[#00AEEF] uppercase mb-3">Câu Chuyện Sản Phẩm</p>
        <h2 className="text-2xl font-bold text-white max-w-2xl mx-auto leading-tight">Khám phá {product.name}</h2>
      </div>
      {product.sections.map((section, i) => {
        const cleanHtml = section.content
          ? DOMPurify.sanitize(section.content, {
              ADD_TAGS: ["iframe"],
              ADD_ATTR: ["target", "rel", "style", "width", "height"],
              ALLOWED_ATTR: [
                "href", "src", "alt", "title", "target", "rel",
                "style", "class", "width", "height",
                "colspan", "rowspan", "allowfullscreen", "frameborder",
              ],
            })
          : "";
        const isHtml = cleanHtml.includes("<");
        const isImageLeft = i % 2 === 0;

        return (
          <div key={i} className={`py-14 ${i % 2 === 0 ? "bg-white" : "bg-stone-50"}`}>
            <div className="max-w-[1100px] mx-auto px-6 md:px-10">
              <div className={`flex flex-col lg:flex-row gap-10 items-center ${!isImageLeft ? "lg:flex-row-reverse" : ""}`}>
                <div className="w-full lg:w-[60%]">
                  <div
                    className="rounded-2xl overflow-hidden border-2 border-gray-300 shadow-md"
                    style={{
                      backgroundColor: "#E8ECF3",
                      backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
                      backgroundSize: "20px 20px",
                    }}
                  >
                    {section.image ? (
                      <img
                        src={section.image}
                        alt={section.title}
                        className="w-full h-auto block"
                        loading="eager"
                        decoding="sync"
                        style={{
                          display: "block",
                          imageRendering: "-webkit-optimize-contrast",
                          minHeight: 200,
                        }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ minHeight: 300 }}>
                        <i className="ri-image-line text-5xl text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-[40%]">
                  <p className="text-[72px] font-bold text-stone-100 leading-none mb-2 select-none">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="text-2xl font-bold text-gray-900 leading-tight mb-4 -mt-5">{section.title}</h3>
                  <div className="w-10 h-1 bg-[#004A99] rounded-full mb-4" />
                  {isHtml ? (
                    <div
                      className="text-gray-600 leading-relaxed w-full overflow-hidden
                        [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ul>li]:mb-1
                        [&_ol]:list-decimal [&_ol]:pl-4 [&_ol>li]:mb-1
                        [&_strong]:font-bold [&_strong]:text-gray-900 [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2
                        [&_img]:rounded-xl [&_img]:my-2 [&_img]:block [&_img]:max-w-full [&_img]:h-auto [&_img]:border [&_img]:border-gray-200 [&_img]:shadow-sm"
                      style={{ maxWidth: '100%' }}
                      dangerouslySetInnerHTML={{ __html: cleanHtml }}
                    />
                  ) : (
                    <p className="text-gray-600 text-sm leading-relaxed">{section.content}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Applications Section (legacy) ─────────────────────────────────────────── */
const CatalogApplications = ({ product }: { product: Product }) => {
  const apps = (product as unknown as { applications?: { image?: string; image_url?: string; title?: string; description?: string }[] }).applications;

  const cards = (() => {
    if (apps && apps.length > 0) {
      return apps.map((app) => ({
        image: app.image_url ?? app.image ?? product.images[0] ?? "",
        title: app.title ?? "",
        desc: app.description ?? "",
        isHtml: (app.description ?? "").includes("<"),
      }));
    }
    if (product.sections && product.sections.length > 0) {
      return product.sections.map((sec, i) => ({
        image: sec.image ?? product.images[i % product.images.length] ?? "",
        title: sec.title,
        desc: sec.accent ?? `Ứng dụng thực tiễn trong ${sec.title}`,
        isHtml: false,
      }));
    }
    return [];
  })();

  if (cards.length === 0) return null;

  return (
    <section className="py-14 bg-[#F8FAFE]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="mb-10">
          <p className="text-xs font-bold tracking-[0.25em] text-[#004A99] uppercase mb-2">Ứng Dụng</p>
          <h2 className="text-2xl font-bold text-gray-900">Ứng dụng thực tế</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
              <div
                className="overflow-hidden rounded-t-2xl border-b-2 border-gray-200"
                style={{
                  backgroundColor: "#E8ECF3",
                  backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                }}
              >
                {card.image ? (
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-auto block"
                    loading="eager"
                    decoding="sync"
                    style={{
                      display: "block",
                      imageRendering: "-webkit-optimize-contrast",
                      minHeight: 160,
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-4xl text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>
                {card.isHtml ? (
                  <div
                    className="text-sm text-gray-500 line-clamp-3 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(card.desc, {
                        ALLOWED_TAGS: ["span", "b", "strong", "em", "p", "br", "ul", "ol", "li"],
                        ALLOWED_ATTR: ["style", "class"],
                      }),
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 line-clamp-3">{card.desc}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Patents / Certifications Section (legacy) ──────────────────────────────── */
const CatalogPatents = ({ product }: { product: Product }) => {
  const patents = product.patents_certifications ?? [];
  const [lightbox, setLightbox] = useState<number | null>(null);
  const withImages = patents.filter((p) => p.image_url);

  if (patents.length === 0) return null;

  const typeLabel: Record<string, { label: string; color: string; icon: string }> = {
    patent: { label: "Bằng Sáng Chế", color: "#004A99", icon: "ri-file-text-line" },
    certification: { label: "Chứng Nhận", color: "#059669", icon: "ri-medal-line" },
    award: { label: "Giải Thưởng", color: "#D97706", icon: "ri-trophy-line" },
  };

  return (
    <section className="py-14 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="mb-10">
          <p className="text-xs font-bold tracking-[0.3em] text-[#004A99] uppercase mb-2">TIN TƯỞNG &amp; CHỨNG NHẬN</p>
          <h2 className="text-2xl font-bold text-gray-900">Bằng Sáng Chế &amp; Chứng Nhận</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {patents.map((item, i) => {
            const meta = typeLabel[item.type] ?? typeLabel.certification;
            const imgIdx = withImages.findIndex((p) => p.image_url === item.image_url && p.title === item.title);
            return (
              <div
                key={i}
                className="flex flex-col items-center text-center p-5 rounded-2xl border border-gray-100 hover:border-[#004A99]/20 transition-all cursor-pointer group"
                onClick={() => imgIdx >= 0 && setLightbox(imgIdx)}
              >
                {item.image_url ? (
                  <div
                    className="w-full overflow-hidden rounded-xl border-2 border-gray-200 mb-3 relative"
                    style={{
                      backgroundColor: "#E8ECF3",
                      backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
                      backgroundSize: "20px 20px",
                    }}
                  >
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-auto block group-hover:scale-105 transition-transform duration-500"
                      loading="eager"
                      decoding="sync"
                      style={{
                        display: "block",
                        imageRendering: "-webkit-optimize-contrast",
                        minHeight: 120,
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/0 group-hover:bg-white/90 rounded-full transition-all opacity-0 group-hover:opacity-100">
                        <i className="ri-zoom-in-line text-gray-700 text-sm" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-3" style={{ background: `${meta.color}15` }}>
                    <i className={`${meta.icon} text-2xl`} style={{ color: meta.color }} />
                  </div>
                )}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5" style={{ background: `${meta.color}12`, color: meta.color }}>
                  {meta.label}
                </span>
                <h4 className="font-bold text-gray-800 text-xs leading-snug">{item.title}</h4>
                {item.description && <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{item.description}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && withImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-3xl w-full bg-white rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-black/20 hover:bg-black/40 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
            <div className="bg-gray-50 flex items-center justify-center" style={{ height: "65vh" }}>
              <img src={withImages[lightbox].image_url ?? ""} alt={withImages[lightbox].title} className="max-w-full max-h-full object-cover" />
            </div>
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-gray-900 text-sm">{withImages[lightbox].title}</h4>
                {withImages[lightbox].description && (
                  <p className="text-xs text-gray-500 mt-0.5">{withImages[lightbox].description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLightbox((v) => ((v ?? 0) - 1 + withImages.length) % withImages.length)}
                  disabled={withImages.length <= 1}
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer disabled:opacity-30"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <span className="text-xs text-gray-400 font-mono">{(lightbox ?? 0) + 1}/{withImages.length}</span>
                <button
                  onClick={() => setLightbox((v) => ((v ?? 0) + 1) % withImages.length)}
                  disabled={withImages.length <= 1}
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer disabled:opacity-30"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ── Catalog Contact Section ──────────────────────────────────────────────── */
const CatalogContactSection = () => (
  <section className="bg-[#F8FAFF] py-10 md:py-14 no-print">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
      <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-10">
        <div className="w-12 h-12 flex items-center justify-center bg-[#004A99]/10 rounded-xl mx-auto mb-4">
          <i className="ri-customer-service-2-line text-[#004A99] text-xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Liên hệ Tư vấn</h3>
        <p className="text-sm text-gray-500 max-w-lg mx-auto mb-6 leading-relaxed">
          Đội ngũ chuyên gia song ngữ Hàn-Việt sẵn sàng hỗ trợ bạn về mọi sản phẩm và giải pháp công nghệ.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href="https://zalo.me/0965180170" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#004A99] text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-[#003580] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-phone-line" />
            +84 965 180 170
          </a>
          <a href="mailto:bizwell@bizwell.com.vn" className="flex items-center gap-2 border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold text-sm hover:border-[#004A99]/30 hover:text-[#004A99] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-mail-line" />
            bizwell@bizwell.com.vn
          </a>
        </div>
        <p className="text-[11px] text-gray-400 mt-4">
          Phản hồi nhanh chóng · Hỗ trợ kỹ thuật trực tiếp từ nhà sản xuất Hàn Quốc
        </p>
      </div>
    </div>
  </section>
);

const CatalogView = ({ product }: Props) => {
  const builderPages = ((product as unknown as Record<string, unknown>).builder_pages ?? []) as BuilderPage[];
  const hasBuilderContent = builderPages.length > 0 && builderPages.some((p) => p.blocks.length > 0);
  const catalogBuilderPages = ((product as unknown as Record<string, unknown>).catalog_builder_pages ?? []) as BuilderPage[];
  const hasCatalogBuilderContent = catalogBuilderPages.length > 0 && catalogBuilderPages.some((p) => p.blocks.length > 0);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 16mm 12mm; }
        }
      `}</style>

      <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
        {/* Header */}
        <CatalogHeader />

        {/* Hero banner */}
        <CatalogHero product={product} />

        {/* Basic info: media gallery + description */}
        <CatalogInfoPanel product={product} />

        {/* Catalog Builder content (if any) */}
        {hasCatalogBuilderContent ? (
          <BuilderRenderer pages={catalogBuilderPages} productName={product.name} />
        ) : hasBuilderContent ? (
          <BuilderRenderer pages={builderPages} productName={product.name} />
        ) : (
          <>
            <CatalogStory product={product} />
            <CatalogApplications product={product} />
            <CatalogPatents product={product} />
          </>
        )}

        {/* Footer */}
        <CatalogFooter />
      </div>

      <PrintFloater product={product} />
    </>
  );
};

export default CatalogView;